import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Eye, FileDown, ImageDown, ArrowUp, ArrowDown, Save, FolderOpen, DownloadCloud } from "lucide-react";
import { CustomerPicker } from "@/components/CustomerPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "ls_menuekarte_draft";

const EMPTY_GANG = { label: "", titel: "", beschreibung: "" };
const DEFAULT_FORM = {
  anlass: "Menü",
  untertitel: "",
  datum: "",
  schluss: "Ich freue mich darauf, diesen Abend für Sie zu kochen.",
  blanko: false,
  gaenge: [
    { label: "VORSPEISE", titel: "", beschreibung: "" },
    { label: "HAUPTGANG", titel: "", beschreibung: "" },
    { label: "DESSERT", titel: "", beschreibung: "" },
  ],
};

export default function MenuekartenTab({ pwd, prefill, onPrefillUsed }) {
  const [form, setForm] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_KEY) || "null");
      return saved && saved.gaenge?.length ? saved : DEFAULT_FORM;
    } catch { return DEFAULT_FORM; }
  });
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState("");
  const [beschnitt, setBeschnitt] = useState(false);
  const [groesse, setGroesse] = useState("10x14");
  const [library, setLibrary] = useState([]);
  const [menuName, setMenuName] = useState("");
  const [kunde, setKunde] = useState({ id: "", name: "" });
  const [editId, setEditId] = useState(null);

  const loadLibrary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/menuekarten`, { headers: { "X-Admin-Password": pwd } });
      setLibrary(data || []);
    } catch { /* Bibliothek ist optional */ }
  }, [pwd]);

  useEffect(() => { loadLibrary(); }, [loadLibrary]);

  const openMenu = useCallback((m) => {
    setForm({
      anlass: m.anlass || "Menü", untertitel: m.untertitel || "", datum: m.datum || "",
      schluss: m.schluss || "", blanko: !!m.blanko,
      gaenge: (m.gaenge || []).length ? m.gaenge : [{ ...EMPTY_GANG }],
    });
    setMenuName(m.name || "");
    setKunde({ id: m.kunde_id || "", name: m.kunde_name || "" });
    setEditId(m.id);
  }, []);

  useEffect(() => {
    if (!prefill) return;
    if (prefill.menu) openMenu(prefill.menu);
    if (prefill.kundeId) {
      setKunde({ id: prefill.kundeId, name: prefill.kundeName || "" });
      if (!prefill.menu) setEditId(null);
    }
    onPrefillUsed?.();
  }, [prefill, openMenu, onPrefillUsed]);

  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(form)); }, [form]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/menuekarten/presets`, { headers: { "X-Admin-Password": pwd } });
        setPresets(data || []);
      } catch { /* Vorlagen sind optional */ }
    })();
  }, [pwd]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setGang = (idx, k, v) => setForm((f) => ({
    ...f, gaenge: f.gaenge.map((g, i) => (i === idx ? { ...g, [k]: v } : g)),
  }));
  const addGang = () => setForm((f) => ({ ...f, gaenge: [...f.gaenge, { ...EMPTY_GANG }] }));
  const delGang = (idx) => setForm((f) => ({ ...f, gaenge: f.gaenge.filter((_, i) => i !== idx) }));
  const moveGang = (idx, dir) => setForm((f) => {
    const next = [...f.gaenge];
    const to = idx + dir;
    if (to < 0 || to >= next.length) return f;
    [next[idx], next[to]] = [next[to], next[idx]];
    return { ...f, gaenge: next };
  });

  const loadPreset = (key) => {
    const p = presets.find((x) => x.key === key);
    if (!p) return;
    setForm({
      anlass: p.anlass, untertitel: p.untertitel, datum: p.datum,
      schluss: p.schluss, blanko: p.blanko, gaenge: p.gaenge.length ? p.gaenge : [{ ...EMPTY_GANG }],
    });
    toast.success("Vorlage geladen");
  };

  const request = async (fmt, seite, mode) => {
    if (form.gaenge.length === 0) { toast.error("Mindestens einen Gang eintragen"); return; }
    const tag = `${fmt}-${seite}-${mode}`;
    setBusy(tag);
    try {
      const res = await axios.post(`${API}/admin/menuekarten/render`, form, {
        params: { fmt, seite, beschnitt, groesse },
        headers: { "X-Admin-Password": pwd },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      if (mode === "view") {
        window.open(url, "_blank", "noopener");
      } else {
        const a = document.createElement("a");
        a.href = url;
        const base = (form.anlass || "menuekarte").toLowerCase().replace(/[^a-z0-9]+/g, "-");
        a.download = `menuekarte-${base}-${groesse === "10x14" ? "10x14" : "120x210"}-${seite}${beschnitt ? "-mit-beschnitt" : ""}.${fmt}`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      let msg = err.message;
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch { /* noop */ }
      }
      toast.error("Fehler: " + msg);
    } finally {
      setBusy("");
    }
  };

  const saveMenu = async () => {
    if (!form.gaenge.length) { toast.error("Mindestens einen Gang eintragen"); return; }
    const body = { ...form, name: menuName.trim() || form.anlass, kunde_id: kunde.id || "" };
    setBusy("save");
    try {
      if (editId) {
        await axios.put(`${API}/admin/menuekarten/${editId}`, body, { headers: { "X-Admin-Password": pwd } });
        toast.success("Menü aktualisiert");
      } else {
        const { data } = await axios.post(`${API}/admin/menuekarten`, body, { headers: { "X-Admin-Password": pwd } });
        setEditId(data.id);
        toast.success(kunde.name ? `Menü bei ${kunde.name} gespeichert` : "Menü gespeichert");
      }
      loadLibrary();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusy("");
    }
  };

  const deleteMenu = async (m) => {
    if (!window.confirm(`Menü „${m.name}" löschen?`)) return;
    try {
      await axios.delete(`${API}/admin/menuekarten/${m.id}`, { headers: { "X-Admin-Password": pwd } });
      if (editId === m.id) setEditId(null);
      toast.success("Menü gelöscht");
      loadLibrary();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    }
  };

  const importVorlagen = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/menuekarten/import-vorlagen`, {}, { headers: { "X-Admin-Password": pwd } });
      toast.success(`${data.imported} Karten übernommen — ${data.total} in der Bibliothek`);
      loadLibrary();
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    }
  };

  const Btn = ({ tag, onClick, icon: Icon, children, primary = false, testId }) => (
    <button onClick={onClick} disabled={!!busy} data-testid={testId}
      className={`inline-flex items-center gap-2 px-5 py-3 text-[10px] uppercase tracking-[0.15em] transition-colors disabled:opacity-60 ${
        primary ? "bg-[#BA6D52] text-white hover:bg-[#9E5841]"
                : "border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52]"}`}>
      {busy === tag ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />} {children}
    </button>
  );

  return (
    <div data-testid="menuekarten-tab" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Menükarten-Generator</p>
          <p className="text-sm text-[#757067] mt-1 max-w-2xl">
            Gänge eintippen, Druckdateien sofort erzeugen: Format 10 × 14 cm hoch für den
            dm-Fotoservice (100 × 140 mm Endformat + 3 mm Beschnitt), Vorderseite Menü,
            Rückseite mit Logo. Alles druckfertig in 300 dpi.
          </p>
        </div>
        {presets.length > 0 && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Vorlage laden</label>
            <select onChange={(e) => { loadPreset(e.target.value); e.target.value = ""; }}
              data-testid="menuekarte-preset" defaultValue=""
              className="mt-1 block bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm">
              <option value="">Vorlage wählen …</option>
              {presets.map((p) => (
                <option key={p.key} value={p.key}>{p.key === "blanko" ? "Blanko (zum Ausfüllen)" : p.anlass}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="border border-[#E3DECE] bg-white p-5 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">
            <FolderOpen size={13} /> Gespeicherte Menüs ({library.length})
          </p>
          <button onClick={importVorlagen} data-testid="menuekarte-import-vorlagen"
            className="inline-flex items-center gap-2 border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] px-4 py-2 text-[10px] uppercase tracking-[0.15em] transition-colors">
            <DownloadCloud size={13} /> Bestehende Karten übernehmen
          </button>
        </div>
        {library.length === 0 ? (
          <p className="text-sm text-[#757067] italic">
            Noch keine gespeicherten Menüs — unten „Menü speichern" oder die bestehenden Karten übernehmen.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {library.map((m) => (
              <div key={m.id} data-testid={`menue-${m.id}`}
                className={`border p-3 flex items-start justify-between gap-2 ${editId === m.id ? "border-[#BA6D52] bg-[#FAF5F1]" : "border-[#E3DECE]"}`}>
                <button onClick={() => openMenu(m)} data-testid={`menue-${m.id}-open`} className="text-left min-w-0">
                  <span className="block font-serif text-base text-[#2C2A26] truncate">{m.name}</span>
                  <span className="block text-xs text-[#757067] truncate">
                    {[m.kunde_name, m.datum, `${m.gaenge?.length || 0} Gänge`].filter(Boolean).join(" · ")}
                  </span>
                </button>
                <button onClick={() => deleteMenu(m)} data-testid={`menue-${m.id}-delete`} title="löschen"
                  className="border border-red-200 text-red-700 hover:bg-red-50 p-1.5 shrink-0 transition-colors">
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border border-[#E3DECE] bg-[#FAFAF7] p-5 md:p-6 space-y-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Kopf der Karte</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Anlass / Titel</label>
            <input type="text" value={form.anlass} onChange={(e) => set("anlass", e.target.value)}
              data-testid="menuekarte-anlass" placeholder="Geburtstags-Dinner"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Untertitel</label>
            <input type="text" value={form.untertitel} onChange={(e) => set("untertitel", e.target.value)}
              data-testid="menuekarte-untertitel" placeholder="exklusiv für Herr und Frau Muster · 8 Personen"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Datum (frei formuliert)</label>
            <input type="text" value={form.datum} onChange={(e) => set("datum", e.target.value)}
              data-testid="menuekarte-datum" placeholder="13. MÄRZ 2027"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Schlusssatz</label>
            <input type="text" value={form.schluss} onChange={(e) => set("schluss", e.target.value)}
              data-testid="menuekarte-schluss"
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm" />
          </div>
          <label className="flex items-end gap-2 text-xs text-[#757067] cursor-pointer pb-2">
            <input type="checkbox" checked={form.blanko} onChange={(e) => set("blanko", e.target.checked)}
              data-testid="menuekarte-blanko" className="accent-[#BA6D52]" />
            Blanko — nur Gang-Bezeichnung und Schreiblinien
          </label>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">
            Gänge ({form.gaenge.length} von max. 7)
          </p>
          <button onClick={addGang} disabled={form.gaenge.length >= 7} data-testid="menuekarte-add-gang"
            className="inline-flex items-center gap-2 border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] disabled:opacity-40 px-4 py-2 text-[10px] uppercase tracking-[0.15em] transition-colors">
            <Plus size={13} /> Gang hinzufügen
          </button>
        </div>

        {form.gaenge.map((g, idx) => (
          <div key={idx} data-testid={`menuekarte-gang-${idx}`} className="border border-[#E3DECE] bg-white p-4">
            <div className="flex items-start gap-3">
              <span className="font-mono text-xs text-[#BA6D52] pt-3 w-6 shrink-0">{idx + 1}.</span>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-[9px] uppercase tracking-[0.2em] text-[#757067]">Bezeichnung</label>
                  <input type="text" value={g.label} onChange={(e) => setGang(idx, "label", e.target.value)}
                    data-testid={`menuekarte-gang-${idx}-label`} placeholder="VORSPEISE"
                    className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm" />
                </div>
                <div>
                  <label className="text-[9px] uppercase tracking-[0.2em] text-[#757067]">Gericht</label>
                  <input type="text" value={g.titel} onChange={(e) => setGang(idx, "titel", e.target.value)}
                    data-testid={`menuekarte-gang-${idx}-titel`} placeholder="Soufflierte Polenta · Jakobsmuscheln"
                    className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[9px] uppercase tracking-[0.2em] text-[#757067]">Beschreibung</label>
                  <input type="text" value={g.beschreibung} onChange={(e) => setGang(idx, "beschreibung", e.target.value)}
                    data-testid={`menuekarte-gang-${idx}-beschreibung`} placeholder="Kaviar · Beurre blanc · Kresse"
                    className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm" />
                </div>
              </div>
              <div className="flex flex-col gap-1 pt-5 shrink-0">
                <button onClick={() => moveGang(idx, -1)} data-testid={`menuekarte-gang-${idx}-up`} title="nach oben"
                  className="border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] p-1.5 transition-colors"><ArrowUp size={12} /></button>
                <button onClick={() => moveGang(idx, 1)} data-testid={`menuekarte-gang-${idx}-down`} title="nach unten"
                  className="border border-[#E3DECE] hover:border-[#BA6D52] hover:text-[#BA6D52] p-1.5 transition-colors"><ArrowDown size={12} /></button>
                <button onClick={() => delGang(idx)} data-testid={`menuekarte-gang-${idx}-delete`} title="entfernen"
                  className="border border-red-200 text-red-700 hover:bg-red-50 p-1.5 transition-colors"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border border-[#E3DECE] bg-white p-5 md:p-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-4">
          {editId ? "Gespeichertes Menü aktualisieren" : "Menü speichern"}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Name für die Bibliothek</label>
            <input type="text" value={menuName} onChange={(e) => setMenuName(e.target.value)}
              data-testid="menuekarte-name" placeholder={form.anlass || "Menüname"}
              className="mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Kunde (optional)</label>
            <div className="mt-1">
              <CustomerPicker
                pwd={pwd}
                value={kunde.name}
                onChange={(v) => setKunde({ id: "", name: v })}
                onSelect={(k) => setKunde({ id: k.id, name: k.betrieb })}
                placeholder="Kunde zuordnen …"
                testId="menuekarte-kunde"
              />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <button onClick={saveMenu} disabled={!!busy} data-testid="menuekarte-save"
              className="inline-flex items-center gap-2 bg-[#2C2A26] text-white hover:bg-black disabled:opacity-60 px-5 py-2.5 text-[10px] uppercase tracking-[0.15em] transition-colors">
              {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {editId ? "Aktualisieren" : "Menü speichern"}
            </button>
            {editId && (
              <button onClick={() => { setEditId(null); setMenuName(""); }} data-testid="menuekarte-save-new"
                className="border border-[#E3DECE] hover:border-[#BA6D52] px-4 py-2.5 text-[10px] uppercase tracking-[0.15em]">
                Als neues
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="border border-[#BA6D52] bg-[#FAF5F1] p-5 md:p-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-3">Druckdateien</p>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-[10px] uppercase tracking-[0.15em] text-[#757067] mr-1">Format</span>
          {[["10x14", "100 × 140 mm"], ["12x21", "120 × 210 mm"]].map(([key, label]) => (
            <button key={key} onClick={() => setGroesse(key)} data-testid={`menuekarte-groesse-${key}`}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] border transition-colors ${
                groesse === key ? "border-[#BA6D52] bg-[#BA6D52] text-white"
                               : "border-[#E3DECE] text-[#757067] hover:border-[#BA6D52]"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-3">
          <Btn tag="pdf-beide-view" testId="menuekarte-preview" primary icon={Eye}
            onClick={() => request("pdf", "beide", "view")}>Vorschau (beide Seiten)</Btn>
          <Btn tag="pdf-vorderseite-download" testId="menuekarte-pdf-front" icon={FileDown}
            onClick={() => request("pdf", "vorderseite", "download")}>PDF Vorderseite</Btn>
          <Btn tag="pdf-rueckseite-download" testId="menuekarte-pdf-back" icon={FileDown}
            onClick={() => request("pdf", "rueckseite", "download")}>PDF Rückseite</Btn>
          <Btn tag="jpg-vorderseite-download" testId="menuekarte-jpg-front" icon={ImageDown}
            onClick={() => request("jpg", "vorderseite", "download")}>JPG Vorderseite</Btn>
          <Btn tag="jpg-rueckseite-download" testId="menuekarte-jpg-back" icon={ImageDown}
            onClick={() => request("jpg", "rueckseite", "download")}>JPG Rückseite</Btn>
        </div>
        <label className="flex items-center gap-2 mt-4 text-xs text-[#757067] cursor-pointer">
          <input type="checkbox" checked={beschnitt} onChange={(e) => setBeschnitt(e.target.checked)}
            data-testid="menuekarte-beschnitt" className="accent-[#BA6D52]" />
          JPG mit 3 mm Beschnittzugabe (nur nötig, wenn dm es ausdrücklich verlangt)
        </label>
        <p className="text-xs text-[#757067] mt-3 leading-relaxed">
          {groesse === "10x14"
            ? "dm-Fotoservice: Grußkarte / Karte Klassik · 100 × 140 mm Endformat + 3 mm Beschnitt · Premium matt 300 g/m²"
            : "DIN-lang-Format: 120 × 210 mm Endformat + 3 mm Beschnitt · für Online-Druckereien, mattes Papier ab 300 g/m²"}
          {" · Vorder- und Rückseite. Deine Eingaben bleiben im Browser gespeichert."}
        </p>
      </div>
    </div>
  );
}
